import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchProvisionalFileList } from './apiHelper';
import { ProvisionalFileItem } from './types';

/** Holds the org's provisional file list, keyed by org so a previous org's files never render under a new one. */
export function useProvisionalFiles({ orgId, enabled }: { orgId: string; enabled: boolean }) {
    const [held, setHeld] = useState<{ orgId: string; files: ProvisionalFileItem[] }>({ orgId: '', files: [] });
    const requestRef = useRef(0);
    const files = held.orgId === orgId ? held.files : [];

    const refetch = useCallback(async () => {
        if (!enabled || !orgId) return;
        const requestId = ++requestRef.current;
        const result = await fetchProvisionalFileList(orgId);
        if (requestId !== requestRef.current) return;
        setHeld({ orgId, files: result.success ? result.files : [] });
    }, [enabled, orgId]);

    useEffect(() => {
        if (!enabled || !orgId) {
            requestRef.current += 1;
            return;
        }
        refetch();
    }, [enabled, orgId, refetch]);

    /** Removes a just-deleted file at once, so it never reappears while the refetch is in flight. */
    const dropFile = useCallback(
        (docId: string) => setHeld((prev) => (prev.orgId === orgId ? { orgId, files: prev.files.filter((f) => f.docId !== docId) } : prev)),
        [orgId],
    );

    return { files, refetch, dropFile };
}
